import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, List, ListItem, ListItemText,
  Chip, Stack, IconButton, Tooltip, Divider
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import ArticleIcon from '@mui/icons-material/Article';
import SummarizeIcon from '@mui/icons-material/Summarize';
import { listRecentCalls } from '../../../services/callsApi';
import TranscriptReviewDialog from '../../call/TranscriptReviewDialog';
import CallSummaryDialog from '../../call/CallSummaryDialog';

function formatDuration(sec) {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function stateTone(state) {
  if (state === 'ended')  return 'success';
  if (state === 'failed') return 'error';
  return 'default';
}

export default function RecentCallsWidget({ token, onNavigateToClient }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transcriptTarget, setTranscriptTarget] = useState(null);
  const [summaryTarget, setSummaryTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { calls: list } = await listRecentCalls({ token, limit: 8 });
        if (cancelled) return;
        setCalls(list || []);
      } catch (err) {
        if (!cancelled) console.warn('[RecentCallsWidget] load failed:', err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading || calls.length === 0) return null;

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <VideocamIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Recent video calls</Typography>
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">{calls.length} in the last 30 days</Typography>
        </Box>
        <Divider sx={{ mb: 0.5 }} />
        <List dense disablePadding>
          {calls.slice(0, 5).map((c) => {
            const client = c.clientId;
            const name = client && typeof client === 'object'
              ? [client.firstName, client.lastName].filter(Boolean).join(' ') || client.email || 'Client'
              : 'Client';
            const hasSummary = !!c.aiSummary?.generatedAt || !!c.finalSummary?.text;
            const hasRisk = (c.aiSummary?.risks || []).length > 0;
            return (
              <ListItem
                key={c._id}
                sx={{ px: 0, cursor: onNavigateToClient && client?._id ? 'pointer' : 'default' }}
                onClick={() => onNavigateToClient && client?._id && onNavigateToClient(client._id)}
                secondaryAction={
                  <Stack direction="row" spacing={0.25}>
                    <Tooltip title="View transcript">
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setTranscriptTarget(c); }}
                          disabled={c.state !== 'ended' && c.state !== 'failed'}
                        >
                          <ArticleIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="View summary">
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); setSummaryTarget(c); }}
                          disabled={c.state !== 'ended' && c.state !== 'failed'}
                        >
                          <SummarizeIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{name}</Typography>
                      <Chip label={c.state} size="small" variant="outlined" color={stateTone(c.state)} sx={{ height: 18 }} />
                      {hasRisk && <Chip label="flagged" size="small" color="warning" sx={{ height: 18 }} />}
                      {!hasSummary && c.state === 'ended' && (
                        <Chip label="needs review" size="small" variant="outlined" sx={{ height: 18 }} />
                      )}
                    </Stack>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {new Date(c.createdAt).toLocaleString()} · {formatDuration(c.durationSec)}
                      {c.aiSummary?.freeform && ` · ${c.aiSummary.freeform.slice(0, 80)}${c.aiSummary.freeform.length > 80 ? '…' : ''}`}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </Paper>

      <TranscriptReviewDialog
        open={!!transcriptTarget}
        onClose={() => setTranscriptTarget(null)}
        token={token}
        liveCallId={transcriptTarget?._id}
        callMeta={transcriptTarget}
      />
      <CallSummaryDialog
        open={!!summaryTarget}
        onClose={() => setSummaryTarget(null)}
        token={token}
        liveCallId={summaryTarget?._id}
        callMeta={summaryTarget}
      />
    </>
  );
}
